import { API_ROOT } from '../../config';
import { VerificationError, VerificationStatus } from '../../modules/provider/PaymentSettings.types';
import SimpleCache from '../../modules/SimpleCache';
import ssFetch, { ssFetchJSON } from '../../modules/ssFetch';
import { DateString } from '../../types';
import { DateTimeStamp } from '../../types/dateTime';

export const IdentityVerificationCache = new SimpleCache(15 * 60);

/**
 * See the ProviderVerificationDetailsRetrieveSerializer in backend
 */
export interface IdentityVerificationStatus {
  address_city: string | null;
  address_country: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_postal_code: string | null;
  address_state: string | null;
  business_name: string | null;
  creation_time: DateTimeStamp;
  dob: DateString | null;
  ein_last4: string | null;
  errors: VerificationError[];
  fields_needed: string[];
  first_name: string | null;
  has_address: boolean;
  has_full_ssn: boolean;
  in_probation: boolean;
  last_name: string | null;
  modification_time: DateTimeStamp;
  needs_company_ein: boolean;
  needs_full_ssn: boolean;
  needs_legal_document: boolean;
  needs_mailing_address_charges: boolean;
  needs_valid_address: string[];
  needs_verification_details: boolean;
  payments_enabled: boolean;
  person_company_ownership_percent: number | null;
  person_job_title: string | null;
  phone_number: string | null;
  ssn_last4: `${number}${number}${number}${number}` | null;
  status: VerificationStatus;
}

/**
 * The shape of the payload to send when saving verification details
 * Since this endpoint acts like a PATCH (even though it is POST)
 * all the properties are optional and this may take different shapes
 */
export interface IdentityVerificationSavePayload {
  dob?: string;
  providerId: number | string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  ein?: string | null;
  business_name?: string | null;
  person_job_title?: string | null;
  person_company_ownership_percent?: number | string | null;
  ssn?: string;
  ssn_last4?: string;
  address_city?: string;
  address_line1?: string;
  address_line2?: string | null;
  address_postal_code?: string;
  address_state?: string;
}

export const getIdentityVerification = async (providerId: number) => {
  const url = new URL(
    `/api/v1/providers/${providerId}/verification-details.json`,
    API_ROOT,
  );

  return ssFetchJSON<IdentityVerificationStatus>(url.toString(), {
    method: 'GET',
    ssCache: IdentityVerificationCache,
  });
};

export const saveIdentityVerification = async (
  providerId: number,
  data: IdentityVerificationSavePayload,
) => {
  const url = new URL(
    `/api/v1/providers/${providerId}/verification-details.json`,
    API_ROOT,
  );

  return ssFetch<IdentityVerificationStatus>(url.toString(), {
    method: 'POST',
    body: data,
  });
};

export const isIdentityVerified = async (providerId: number) => {
  const { status: verificationStatus } = await getIdentityVerification(providerId);
  return (
    // eslint-disable-next-line eqeqeq
    verificationStatus != VerificationStatus.EMPTY
    // eslint-disable-next-line eqeqeq
    && verificationStatus != VerificationStatus.UNVERIFIED
  );
};
